import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export type EntitlementAssistantContext = {
  open: boolean;
  content: React.ReactNode | null;
  loading: boolean;
  maximized?: boolean;
  maximizedVertical?: boolean;
  onBottom?: boolean;
  /** Route predicate. Missing means the panel may show on every page. */
  showOn?: (pathname: string) => boolean;
  /** Environment the content was built for; a switch destroys the context. */
  environmentId?: string;
  /** Monotonic order stamp; the highest wins when several panels qualify. */
  openSequence: number;
};

export type EntitlementAssistantOpenOptions = Pick<
  EntitlementAssistantContext,
  'showOn' | 'environmentId'
>;

export type EntitlementAssistantState = {
  contexts: Record<string, EntitlementAssistantContext>;

  openDrawer: (
    key: string,
    content?: React.ReactNode,
    maximized?: boolean,
    maximizedVertical?: boolean,
    onBottom?: boolean,
    options?: EntitlementAssistantOpenOptions,
  ) => void;
  isOpen: (key: string) => boolean;
  isMaximized: (key: string) => boolean;
  hideDrawer: (key: string) => void;
  closeDrawer: (key: string) => void;
  showForPathname: (pathname: string) => void;
  closeDrawersForOtherEnvironments: (environmentId?: string) => void;
  setLoading: (key: string, loading: boolean) => void;
  toggleMaximizeDrawer: (key: string) => void;
  toggleMaximizeVerticalDrawer: (key: string) => void;
  toggleBottomDrawer: (key: string) => void;
  setContent: (key: string, content: React.ReactNode | null) => void;
};

type Contexts = Record<string, EntitlementAssistantContext>;

let lastOpenSequence = 0;

const nextOpenSequence = () => {
  lastOpenSequence += 1;
  return lastOpenSequence;
};

const hideAll = (contexts: Contexts): Contexts => {
  return Object.keys(contexts).reduce((acc, ctxKey) => {
    acc[ctxKey] = { ...contexts[ctxKey], open: false };
    return acc;
  }, {} as Contexts);
};

const isAllowedOn = (
  context: EntitlementAssistantContext,
  pathname: string,
) => {
  return context.showOn ? context.showOn(pathname) : true;
};

const pickMostRecentlyOpened = (contexts: Contexts, keys: string[]) => {
  return keys.reduce<string | undefined>((winner, key) => {
    if (!winner || contexts[key].openSequence > contexts[winner].openSequence) {
      return key;
    }
    return winner;
  }, undefined);
};

export const useEntitlementAssistantStore = create<EntitlementAssistantState>()(
  devtools(
    (set, get) => ({
      contexts: {},
      isOpen: key => !!get().contexts[key]?.open,
      isMaximized: key => !!get().contexts[key]?.maximized,

      openDrawer: (
        key,
        content = null,
        maximized = false,
        maximizedVertical = true,
        onBottom = false,
        options = {},
      ) =>
        set(state => {
          const existing = state.contexts[key];
          const openSequence = nextOpenSequence();
          const next: EntitlementAssistantContext = existing
            ? { ...existing, open: true, openSequence }
            : {
                open: true,
                content,
                loading: false,
                maximized,
                maximizedVertical,
                onBottom,
                showOn: options.showOn,
                environmentId: options.environmentId,
                openSequence,
              };

          return {
            contexts: {
              ...hideAll(state.contexts),
              [key]: next,
            },
          };
        }),

      hideDrawer: key =>
        set(state => {
          const existing = state.contexts[key];
          if (!existing || !existing.open) {
            return state;
          }
          return {
            contexts: {
              ...state.contexts,
              [key]: { ...existing, open: false },
            },
          };
        }),

      showForPathname: pathname =>
        set(state => {
          const keys = Object.keys(state.contexts);
          const allowed = keys.filter(key =>
            isAllowedOn(state.contexts[key], pathname),
          );
          const visibleKey = pickMostRecentlyOpened(state.contexts, allowed);

          const unchanged = keys.every(
            key => state.contexts[key].open === (key === visibleKey),
          );
          if (unchanged) {
            return state;
          }

          return {
            contexts: keys.reduce((acc, key) => {
              acc[key] = { ...state.contexts[key], open: key === visibleKey };
              return acc;
            }, {} as Contexts),
          };
        }),

      closeDrawersForOtherEnvironments: environmentId =>
        set(state => {
          const keys = Object.keys(state.contexts);
          const kept = keys.filter(key => {
            const bound = state.contexts[key].environmentId;
            return bound === undefined || bound === environmentId;
          });
          if (kept.length === keys.length) {
            return state;
          }
          return {
            contexts: kept.reduce((acc, key) => {
              acc[key] = state.contexts[key];
              return acc;
            }, {} as Contexts),
          };
        }),

      toggleMaximizeVerticalDrawer: (key: string) =>
        set(state => ({
          contexts: {
            ...state.contexts,
            [key]: {
              ...state.contexts[key],
              maximizedVertical: !state.contexts[key].maximizedVertical,
            },
          },
        })),

      toggleMaximizeDrawer: (key: string) =>
        set(state => ({
          contexts: {
            ...state.contexts,
            [key]: {
              ...state.contexts[key],
              maximized: !state.contexts[key].maximized,
              onBottom: false,
            },
          },
        })),

      toggleBottomDrawer: (key: string) =>
        set(state => ({
          contexts: {
            ...state.contexts,
            [key]: {
              ...state.contexts[key],
              onBottom: !state.contexts[key].onBottom,
              maximized: false,
            },
          },
        })),

      closeDrawer: key =>
        set(state => ({
          contexts: {
            ...Object.keys(state.contexts)
              .filter(ctxKey => ctxKey !== key)
              .reduce((acc, ctxKey) => {
                acc[ctxKey] = state.contexts[ctxKey];
                return acc;
              }, {} as Contexts),
          },
        })),

      setLoading: (key, loading) =>
        set(state => ({
          contexts: {
            ...state.contexts,
            [key]: {
              ...state.contexts[key],
              loading,
            },
          },
        })),

      setContent: (key, content) =>
        set(state => ({
          contexts: {
            ...state.contexts,
            [key]: {
              ...state.contexts[key],
              content,
            },
          },
        })),
    }),
    { name: 'EntitlementAssistantStore' },
  ),
);
